import { useCommonLabels } from '@hooks/useCommonLabels';
import CloserIcon from '@components/icons/CloserIcon/CloserIcon';
import Router from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import LinkReader from '../LinkReader/LinkReader';
import styles from './DynamicBanner.module.scss';

const DYNAMIC_BANNER_CLOSED_KEY = 'dynamicBannerClosed';
const DEFAULT_BANNER_BACKGROUND_COLOR = '#bdecf9';

const normalizePathChunk = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replaceAll(/^\/+|\/+$/g, '');

const parseBannerPath = (path?: string[] | string): string[] => {
  if (!path) {
    return [];
  }

  if (Array.isArray(path)) {
    return path;
  }

  try {
    const parsedPath = JSON.parse(path);
    return Array.isArray(parsedPath) ? parsedPath : [path];
  } catch {
    return [path];
  }
};

const DynamicBanner = (): JSX.Element | null => {
  const { dynamicBanner } = useCommonLabels();
  const [currentPath, setCurrentPath] = useState('');
  const [isClosed, setIsClosed] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  useEffect(() => {
    setIsClosed(sessionStorage.getItem(DYNAMIC_BANNER_CLOSED_KEY) === 'true');
  }, []);

  const handleClose = () => {
    sessionStorage.setItem(DYNAMIC_BANNER_CLOSED_KEY, 'true');
    setIsClosed(true);
  };

  useEffect(() => {
    const getRouterAsPath = () => {
      try {
        return Router.asPath;
      } catch {
        return undefined;
      }
    };

    const updatePath = (nextPath?: string) => {
      const pathFromRouter = nextPath || getRouterAsPath();
      const fallbackPath = globalThis.window === undefined ? '' : globalThis.window.location.pathname;
      setCurrentPath((pathFromRouter || fallbackPath || '').split('?')[0].toLowerCase());
    };

    updatePath();

    if (!Router?.events) {
      return;
    }

    Router.events.on('routeChangeComplete', updatePath);

    return () => {
      Router.events.off('routeChangeComplete', updatePath);
    };
  }, []);

  const shouldDisplayBanner = useMemo(() => {
    if (!dynamicBanner?.enabled || !dynamicBanner?.content?.value?.document || !currentPath) {
      return false;
    }

    const allowedPathChunks = parseBannerPath(dynamicBanner.path).map(normalizePathChunk).filter(Boolean);

    if (!allowedPathChunks.length) {
      return false;
    }

    return allowedPathChunks.some(pathChunk => currentPath.includes(pathChunk));
  }, [currentPath, dynamicBanner?.content?.value?.document, dynamicBanner?.enabled, dynamicBanner?.path]);

  if (!shouldDisplayBanner || isClosed) {
    return null;
  }

  const bannerBackgroundColor = dynamicBanner?.backgroundColor?.trim() || DEFAULT_BANNER_BACKGROUND_COLOR;

  return (
    <aside
      className={styles.bannerOuter}
      data-testid="dynamic-banner"
      style={{ backgroundColor: bannerBackgroundColor }}
    >
      <button
        type="button"
        className={styles.closeButton}
        aria-label="Chiudi"
        data-testid="dynamic-banner-close"
        onClick={handleClose}
      >
        <CloserIcon width={12} height={12} />
      </button>
      <div className={styles.bannerInner}>
        {dynamicBanner?.title && <p className={styles.bannerTitle}>{dynamicBanner.title}</p>}
        <div className={`${styles.bannerContent} ${isContentExpanded ? '' : styles.bannerContentTruncated}`}>
          <LinkReader text={dynamicBanner?.content} underline />
        </div>
        {!isContentExpanded && (
          <button
            type="button"
            className={styles.readMoreButton}
            data-testid="dynamic-banner-read-more"
            onClick={() => setIsContentExpanded(true)}
          >
            Leggi di più
          </button>
        )}
      </div>
    </aside>
  );
};

export default DynamicBanner;
